/**
 * Utility for uploading files using presigned URLs
 * Handles direct upload to S3 with progress tracking
 */

/**
 * Uploads a file to S3 using a presigned URL
 * @param {string} presignedUrl - The presigned URL from the backend
 * @param {File|Blob} file - The file to upload
 * @param {string} contentType - The content type of the file
 * @param {Function} onProgress - Optional progress callback (progress: number) => void
 * @param {AbortSignal} abortSignal - Optional AbortSignal to cancel the upload
 * @returns {Promise<void>}
 */
export const uploadToPresignedUrl = async (
  presignedUrl,
  file,
  contentType,
  onProgress = null,
  abortSignal = null
) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Handle abort signal
    if (abortSignal) {
      abortSignal.addEventListener("abort", () => {
        xhr.abort();
        reject(new Error("Upload was aborted"));
      });
    }

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `Upload failed with status ${xhr.status}: ${xhr.statusText}`
          )
        );
      }
    });

    xhr.addEventListener("error", (event) => {
      // Check if it's a CORS error
      if (xhr.status === 0) {
        reject(
          new Error(
            "CORS error: The S3 bucket needs to be configured to allow uploads from this domain. Please contact support."
          )
        );
      } else {
        reject(new Error("Network error during upload"));
      }
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload was aborted"));
    });

    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", contentType);

    // Don't set additional headers as presigned URL handles authentication
    // Setting custom headers might break the signature

    xhr.send(file);
  });
};

/**
 * Gets file extension from content type or filename
 * @param {string} contentType - MIME type
 * @param {string} fileName - Optional filename
 * @returns {string} File extension
 */
export const getFileExtension = (contentType, fileName = "") => {
  // Try to get extension from filename first
  if (fileName) {
    const match = fileName.match(/\.([^.]+)$/);
    if (match) return match[1];
  }

  // Map common content types to extensions
  const typeMap = {
    "video/mp4": "mp4",
    "video/webm": "webm",
    "video/webm;codecs=vp8": "webm",
    "video/webm;codecs=vp9": "webm",
    "video/webm;codecs=h264": "webm",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "application/pdf": "pdf",
  };

  // Check exact match first
  if (typeMap[contentType]) {
    return typeMap[contentType];
  }

  // Check partial match for video types
  if (contentType.startsWith("video/")) {
    if (contentType.includes("webm")) return "webm";
    if (contentType.includes("mp4")) return "mp4";
    return "webm"; // default for video
  }

  // Default fallback
  return "bin";
};

/**
 * Generates a unique filename with timestamp
 * @param {string} contentType - MIME type
 * @param {string} prefix - Optional prefix (e.g., 'video', 'recording')
 * @returns {string} Generated filename
 */
export const generateFileName = (contentType, prefix = "file") => {
  const extension = getFileExtension(contentType);
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const random = Math.random().toString(36).substring(2, 9);
  return `${prefix}-${timestamp}-${random}.${extension}`;
};
